import type { ChatTab } from "../shared/mcp-chat-types";
import type { RestoredTab } from "./mcp-chat-persistence";

/**
 * Overlay the live tabs of still-running turns onto a restored snapshot.
 *
 * KOA-18210 — the panel reloads persisted chat state on every reconnect, and the
 * snapshot cannot know about an in-flight turn: it is written when a turn ENDS.
 * Applying it wholesale erased the thinking step and the live tool transcript of
 * a build that was still running server-side — what users saw as "the chat forgot
 * what it was doing while the work carried on". For a tab whose turn is in flight
 * the in-memory object is authoritative; a live tab absent from the snapshot (its
 * first turn, nothing saved yet) is appended rather than dropped.
 *
 * Order follows the snapshot, so restoring never reshuffles the user's tabs.
 *
 * Deliberately a leaf module: `mcp-chat-persistence` pulls in Axios and the
 * kuika-cl-model URL helpers, which a pure unit test should not have to boot.
 */
export function mergeRestoredTabsWithLiveTurns(restored: RestoredTab[], liveTabs: ChatTab[]): ChatTab[] {
  if (liveTabs.length === 0) return restored;
  const liveById: Map<string, ChatTab> = new Map(liveTabs.map((tab: ChatTab): [string, ChatTab] => [tab.id, tab]));
  const merged: ChatTab[] = restored.map((tab: RestoredTab): ChatTab => liveById.get(tab.id) ?? tab);
  for (const liveTab of liveTabs) {
    if (!merged.some((tab: ChatTab): boolean => tab.id === liveTab.id)) merged.push(liveTab);
  }
  return merged;
}
